import React, { useEffect, useState } from 'react';
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import { addVote, getVoteCount } from './services/voteService';
import './RinkCard.css';

// Helper function to get condition color and label
const getConditionInfo = (condition) => {
  const conditionMap = {
    'Excellente': { color: 'excellent', label: 'Excellente', emoji: '⭐' },
    'Bonne': { color: 'good', label: 'Bonne', emoji: '👍' },
    'Mauvaise': { color: 'bad', label: 'Mauvaise', emoji: '⚠️' },
    'N/A': { color: 'unknown', label: 'Non disponible', emoji: '❓' }
  };

  // Return matched condition, or fallback to displaying the raw string with 'unknown' style
  if (conditionMap[condition]) {
    return conditionMap[condition];
  }

  if (!condition || condition === 'Inconnu') {
    return conditionMap['N/A'];
  }

  // Dynamic condition (e.g. "Entretien en cours", "Hors-saison")
  return { color: 'unknown', label: condition, emoji: 'ℹ️' };
};

// Helper to format date nicely and check if outdated
const formatDate = (dateString) => {
  if (!dateString) return { text: 'Non disponible', isOutdated: false };
  const date = new Date(dateString.replace(' ', 'T'));
  const now = new Date();

  // Calculate days since last update
  const diffTime = now - date;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  const isOutdated = diffDays > 7; // More than 1 week old

  // Get date parts for comparison (ignoring time)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Format time part
  const timeStr = date.toLocaleTimeString('fr-CA', {
    hour: '2-digit',
    minute: '2-digit'
  });

  let text;
  // Check if today or yesterday
  if (dateOnly.getTime() === today.getTime()) {
    text = `Aujourd'hui à ${timeStr}`;
  } else if (dateOnly.getTime() === yesterday.getTime()) {
    text = `Hier à ${timeStr}`;
  } else {
    // Otherwise show full date
    text = date.toLocaleDateString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return { text, isOutdated };
};

const RinkCard = ({ rink, onClose, onSuggestModification }) => {
  const [voteCount, setVoteCount] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [isVoting, setIsVoting] = useState(false);

  // Load user stats from localStorage on mount
  useEffect(() => {
    const stats = JSON.parse(localStorage.getItem('userContributions') || '{"totalSuggestions":0,"approvedSuggestions":0,"totalVotes":0}');
    setUserStats(stats);
  }, []);

  useEffect(() => {
    if (rink && analytics) {
      logEvent(analytics, 'view_item', {
        item_id: rink.id || rink.nom,
        item_name: rink.nom,
        item_category: 'rink',
        location_id: rink.arrondissement?.nom_arr
      });
    }

    // Load vote count
    loadVoteData();
  }, [rink]);

  const loadVoteData = async () => {
    if (!rink) return;

    const rinkId = rink.id || rink.nom;

    // Get official vote count from rink data
    const officialCount = rink.voteCount || 0;

    // Check if user has voted optimistically (before admin computed)
    const optimisticVotes = JSON.parse(localStorage.getItem('optimisticVotes') || '{}');
    const hasOptimisticVote = optimisticVotes[rinkId] ? 1 : 0;

    // Show official count + optimistic vote (if any)
    setVoteCount(officialCount + hasOptimisticVote);

    // Check localStorage for votes with timestamps (24-hour expiration)
    const votedRinks = JSON.parse(localStorage.getItem('votedRinksWithTime') || '{}');
    const voteData = votedRinks[rinkId];

    if (voteData) {
      const voteTime = new Date(voteData.timestamp);
      const now = new Date();
      const hoursSinceVote = (now - voteTime) / (1000 * 60 * 60);

      // Vote expires after 24 hours
      if (hoursSinceVote >= 24) {
        // Vote expired, can vote again
        setHasVoted(false);
        // Clear optimistic vote too
        delete optimisticVotes[rinkId];
        localStorage.setItem('optimisticVotes', JSON.stringify(optimisticVotes));
      } else {
        setHasVoted(true);
      }
    } else {
      setHasVoted(false);
    }
  };

  if (!rink) return null;

  const isOpen = rink.ouvert == 1;
  const conditionInfo = getConditionInfo(rink.condition);
  const dateInfo = formatDate(rink.arrondissement?.date_maj);
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rink.address)}`;

  const handleGetDirections = () => {
    if (analytics) {
      logEvent(analytics, 'get_directions', {
        item_id: rink.id || rink.nom,
        destination: rink.address
      });
    }
  };

  const handleVote = async () => {
    if (hasVoted) return;

    setIsVoting(true);
    try {
      const rinkId = rink.id || rink.nom;

      // Generate anonymous user ID from localStorage or create new one
      let anonymousId = localStorage.getItem('anonymousUserId');
      if (!anonymousId) {
        anonymousId = 'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        localStorage.setItem('anonymousUserId', anonymousId);
      }

      await addVote(rinkId, anonymousId, 'location');

      // Track in localStorage with timestamp for 24-hour expiration
      const votedRinks = JSON.parse(localStorage.getItem('votedRinksWithTime') || '{}');
      votedRinks[rinkId] = { timestamp: new Date().toISOString() };
      localStorage.setItem('votedRinksWithTime', JSON.stringify(votedRinks));

      // Store optimistic vote (will be cleared when admin computes votes)
      const optimisticVotes = JSON.parse(localStorage.getItem('optimisticVotes') || '{}');
      optimisticVotes[rinkId] = true;
      localStorage.setItem('optimisticVotes', JSON.stringify(optimisticVotes));

      // Update local vote count stats
      const stats = JSON.parse(localStorage.getItem('userContributions') || '{"totalSuggestions":0,"approvedSuggestions":0,"totalVotes":0}');
      stats.totalVotes += 1;
      localStorage.setItem('userContributions', JSON.stringify(stats));
      setUserStats(stats);

      // Optimistic UI update - increment vote count immediately
      setVoteCount(prev => prev + 1);
      setHasVoted(true);
    } catch (error) {
      console.error('Error voting:', error);
      alert('Erreur lors du vote');
    } finally {
      setIsVoting(false);
    }
  };

  const handleSuggestModification = () => {
    onSuggestModification(rink);
  };

  return (
    <div className="rink-card animate-slideUp">
      {/* Vote button - top right */}
      <button
        className={`rink-card__vote-button-compact ${hasVoted ? 'voted' : ''}`}
        onClick={handleVote}
        disabled={isVoting || hasVoted}
        title={hasVoted ? 'Déjà voté' : 'Voter pour cette patinoire'}
      >
        <span className="vote-icon">👍</span>
        <span className="vote-count">{voteCount}</span>
      </button>

      {/* Close button */}
      <button className="rink-card__close" onClick={onClose} aria-label="Fermer">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Status badge */}
      <div className={`rink-card__status ${isOpen ? 'rink-card__status--open' : 'rink-card__status--closed'}`}>
        <span className="rink-card__status-dot"></span>
        {isOpen ? 'Ouverte' : 'Fermée'}
      </div>

      {/* Title */}
      <h2 className="rink-card__title">{rink.nom}</h2>

      {/* Arrondissement */}
      <p className="rink-card__arrondissement">{rink.arrondissement?.nom_arr}</p>

      {/* Info rows */}
      <div className="rink-card__info">
        {/* Address */}
        <div className="rink-card__row">
          <span className="rink-card__icon">📍</span>
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rink-card__link"
            onClick={handleGetDirections}
          >
            {rink.address}
          </a>
        </div>

        {/* Condition */}
        <div className="rink-card__row">
          <span className="rink-card__icon">{conditionInfo.emoji}</span>
          <span className="rink-card__label">Condition:</span>
          <span className={`rink-card__condition rink-card__condition--${conditionInfo.color}`}>
            {conditionInfo.label}
          </span>
        </div>

        {/* Last update */}
        <div className="rink-card__row">
          <span className="rink-card__icon">🕐</span>
          <span className="rink-card__label">Mise à jour:</span>
          <span className={`rink-card__value ${dateInfo.isOutdated ? 'rink-card__value--outdated' : ''}`}>
            {dateInfo.text}
            {dateInfo.isOutdated && <span className="rink-card__outdated-badge">Données anciennes</span>}
          </span>
        </div>

        {/* Extra info badges */}
        <div className="rink-card__badges">
          {rink.deblaye === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Déblayée">
              ❄️ Déblayée
            </span>
          )}
          {rink.arrose === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Arrosée">
              💧 Arrosée
            </span>
          )}
          {rink.resurface === '1' && (
            <span className="rink-card__badge rink-card__badge--info" title="Resurfacée">
              ✨ Resurfacée
            </span>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="rink-card__actions">
        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="rink-card__button"
          onClick={handleGetDirections}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
            <circle cx="12" cy="9" r="2.5" />
          </svg>
          Obtenir l'itinéraire
        </a>

        {/* Suggest modification button */}
        <button
          className="rink-card__suggest-button"
          onClick={handleSuggestModification}
        >
          ✏️ Suggérer une modification
        </button>
      </div>

    </div>
  );
};

export default RinkCard;
